/**
 * Domain Layer - TemplateAssignmentPolicy
 * 解放枠テンプレートの割り当てポリシー
 *
 * @description
 * 「どのシフトにどのテンプレートが適用されるか」を決める純粋なルール。
 * 優先順位は 生成済みテンプレート > 医師個別の上書き > デフォルトテンプレート。
 */

import type { ReservationSlotOverride } from './ReservationSlotSettings';
import type { ReservationSlotTargetShift } from './ReservationSlotTargetShift';

/**
 * doctorShiftId をキーとした割り当てテンプレートIDのマップ
 */
export type TemplateAssignmentMap = Record<number, string>;

/**
 * 医師個別の上書き設定を doctorId 引きのMapに変換する
 *
 * @param {readonly ReservationSlotOverride[]} overrides - 上書き設定の配列
 * @returns {Map<number, string>} doctorId → templateId のMap
 */
export const buildOverrideTemplateMap = (
  overrides: readonly ReservationSlotOverride[]
): Map<number, string> => {
  const map = new Map<number, string>();
  for (const override of overrides) {
    if (override.templateId) {
      map.set(override.doctorId, override.templateId);
    }
  }
  return map;
};

/**
 * 医師に適用されるテンプレートIDを解決する
 *
 * @description
 * 個別上書きが登録されていればそれを、なければデフォルトテンプレートを返す。
 * どちらも無い場合は空文字（未選択）を返す。
 *
 * @param {number} doctorId - 対象医師ID
 * @param {ReadonlyMap<number, string>} overrideMap - doctorId → templateId のMap
 * @param {string | null} defaultTemplateId - デフォルトテンプレートID
 * @returns {string} 適用テンプレートID（未解決の場合は空文字）
 */
export const resolveTemplateIdForDoctor = (
  doctorId: number,
  overrideMap: ReadonlyMap<number, string>,
  defaultTemplateId: string | null
): string => {
  return overrideMap.get(doctorId) ?? defaultTemplateId ?? '';
};

/**
 * 対象シフト一覧の初期割り当てを解決する
 *
 * @description
 * 既に解放枠生成済みのシフトは当時使用したテンプレート（sourceTemplateId）を維持し、
 * 未生成のシフトは個別上書き→デフォルトの順で解決する。
 *
 * @param {readonly ReservationSlotTargetShift[]} shifts - 対象シフト配列
 * @param {ReadonlyMap<number, string>} overrideMap - doctorId → templateId のMap
 * @param {string | null} defaultTemplateId - デフォルトテンプレートID
 * @returns {TemplateAssignmentMap} doctorShiftId → templateId の割り当てマップ
 */
export const resolveInitialAssignments = (
  shifts: readonly ReservationSlotTargetShift[],
  overrideMap: ReadonlyMap<number, string>,
  defaultTemplateId: string | null
): TemplateAssignmentMap => {
  const assignments: TemplateAssignmentMap = {};
  for (const shift of shifts) {
    assignments[shift.doctorShiftId] =
      shift.sourceTemplateId ??
      resolveTemplateIdForDoctor(shift.doctorId, overrideMap, defaultTemplateId);
  }
  return assignments;
};

/**
 * 割り当てテンプレートの適用元
 *
 * - manual:     画面上で手動変更された
 * - override:   医師個別の上書き設定に由来する
 * - default:    デフォルトテンプレート（基本設定）に由来する
 * - generated:  既に解放枠生成済みで、当時のテンプレートを引き継いでいる
 * - unresolved: 上書きもデフォルトも無く未解決
 */
export type AssignmentOrigin = 'manual' | 'override' | 'default' | 'generated' | 'unresolved';

/**
 * 割り当てテンプレートの適用元を解決する
 *
 * @description
 * 「そのシフトのテンプレートがどこから来たのか」を判定する。
 * 優先順位は 手動変更 > 生成済み > 個別上書き > 基本設定（デフォルト）> 未解決。
 * 手動変更は利用者の意思による上書きのため、他のどの由来よりも優先される。
 *
 * @param {object} params - 判定に必要な入力
 * @param {number} params.doctorId - 対象医師ID
 * @param {string | null} params.sourceTemplateId - 生成済みの場合に使用したテンプレートID
 * @param {boolean} params.isManual - 画面上で手動変更されたかどうか
 * @param {ReadonlyMap<number, string>} params.overrideMap - doctorId → templateId のMap
 * @param {string | null} params.defaultTemplateId - デフォルトテンプレートID
 * @returns {AssignmentOrigin} 適用元の種別
 */
export const resolveAssignmentOrigin = (params: {
  doctorId: number;
  sourceTemplateId: string | null;
  isManual: boolean;
  overrideMap: ReadonlyMap<number, string>;
  defaultTemplateId: string | null;
}): AssignmentOrigin => {
  const { doctorId, sourceTemplateId, isManual, overrideMap, defaultTemplateId } = params;

  if (isManual) {
    return 'manual';
  }
  if (sourceTemplateId) {
    return 'generated';
  }
  if (overrideMap.has(doctorId)) {
    return 'override';
  }
  if (defaultTemplateId) {
    return 'default';
  }
  return 'unresolved';
};
